/** Matches the tiptap schema (StarterKit, Link, Image, Underline). */
export const RICH_TEXT_NODE_TYPES = [
    'doc',
    'paragraph',
    'text',
    'heading',
    'bulletList',
    'orderedList',
    'listItem',
    'blockquote',
    'codeBlock',
    'hardBreak',
    'horizontalRule',
    'image',
];
export const RICH_TEXT_MARK_TYPES = [
    'bold',
    'italic',
    'underline',
    'strike',
    'code',
    'link',
];
const NODE_TYPES = new Set(RICH_TEXT_NODE_TYPES);
const MARK_TYPES = new Set(RICH_TEXT_MARK_TYPES);
const LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
const IMAGE_PROTOCOLS = new Set(['http:', 'https:']);
const MAX_DEPTH = 20;
const MAX_NODES = 5000;
const MAX_TEXT_LENGTH = 50000;
/**
 * The attributes each type's tiptap schema declares, so nothing else is
 * stored. Types not listed carry no attributes.
 */
const ALLOWED_ATTRS = {
    heading: new Set(['level']),
    orderedList: new Set(['start', 'type']),
    codeBlock: new Set(['language']),
    image: new Set(['src', 'alt', 'title']),
    link: new Set(['href', 'target', 'rel', 'class']),
};
/** Returns the first attribute key `type` does not allow, if any. */
const findDisallowedAttr = (type, attrs) => Object.keys(attrs).find((key) => !ALLOWED_ATTRS[type]?.has(key)) ??
    // tiptap emits class: null for a link; any other value could restyle it.
    (type === 'link' && attrs.class != null ? 'class' : undefined);
const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const hasProtocol = (value, protocols) => {
    if (typeof value !== 'string') {
        return false;
    }
    try {
        return protocols.has(new URL(value).protocol);
    }
    catch {
        return false;
    }
};
const isScalar = (value) => value === null || ['string', 'number', 'boolean'].includes(typeof value);
/** Returns the problems found; empty means valid. */
export const validateRichTextDocument = (value, options = {}) => {
    const errors = [];
    let nodeCount = 0;
    let textLength = 0;
    const visit = (node, path, depth) => {
        if (errors.length > 0) {
            return;
        }
        if (!isPlainObject(node)) {
            errors.push(`${path} must be an object`);
            return;
        }
        nodeCount += 1;
        if (nodeCount > MAX_NODES) {
            errors.push(`document has more than ${MAX_NODES} nodes`);
            return;
        }
        if (depth > MAX_DEPTH) {
            errors.push(`${path} is nested deeper than ${MAX_DEPTH} levels`);
            return;
        }
        const { type, attrs, content, marks, text } = node;
        if (typeof type !== 'string' || !NODE_TYPES.has(type)) {
            errors.push(`${path}.type "${String(type)}" is not allowed`);
            return;
        }
        if (depth > 0 && type === 'doc') {
            errors.push(`${path} cannot contain a nested doc`);
            return;
        }
        if (attrs !== undefined) {
            if (!isPlainObject(attrs) || !Object.values(attrs).every(isScalar)) {
                errors.push(`${path}.attrs must be an object of plain values`);
                return;
            }
            const disallowed = findDisallowedAttr(type, attrs);
            if (disallowed) {
                errors.push(`${path}.attrs.${disallowed} is not allowed`);
                return;
            }
        }
        if (type === 'text') {
            if (typeof text !== 'string' || text.length === 0) {
                errors.push(`${path}.text must be a non-empty string`);
                return;
            }
            if (content !== undefined) {
                errors.push(`${path} is a text node and cannot have content`);
                return;
            }
            textLength += text.length;
            if (textLength > MAX_TEXT_LENGTH) {
                errors.push(`document has more than ${MAX_TEXT_LENGTH} characters`);
                return;
            }
        }
        else if (text !== undefined) {
            errors.push(`${path}.text is only allowed on text nodes`);
            return;
        }
        if (type === 'image') {
            const src = attrs?.src;
            if (!hasProtocol(src, IMAGE_PROTOCOLS) ||
                (options.isAllowedImageSrc && !options.isAllowedImageSrc(src))) {
                errors.push(`${path}.attrs.src is not an allowed image URL`);
                return;
            }
        }
        if (marks !== undefined) {
            if (!Array.isArray(marks)) {
                errors.push(`${path}.marks must be an array`);
                return;
            }
            marks.forEach((mark, index) => {
                const markPath = `${path}.marks[${index}]`;
                if (!isPlainObject(mark) || typeof mark.type !== 'string') {
                    errors.push(`${markPath} must be an object with a type`);
                    return;
                }
                if (!MARK_TYPES.has(mark.type)) {
                    errors.push(`${markPath}.type "${mark.type}" is not allowed`);
                    return;
                }
                if (mark.attrs !== undefined) {
                    if (!isPlainObject(mark.attrs) ||
                        !Object.values(mark.attrs).every(isScalar)) {
                        errors.push(`${markPath}.attrs must be an object of plain values`);
                        return;
                    }
                    const disallowed = findDisallowedAttr(mark.type, mark.attrs);
                    if (disallowed) {
                        errors.push(`${markPath}.attrs.${disallowed} is not allowed`);
                        return;
                    }
                }
                if (mark.type === 'link' &&
                    !hasProtocol(mark.attrs?.href, LINK_PROTOCOLS)) {
                    errors.push(`${markPath}.attrs.href is not an allowed link URL`);
                }
            });
        }
        if (content !== undefined) {
            if (!Array.isArray(content)) {
                errors.push(`${path}.content must be an array`);
                return;
            }
            content.forEach((child, index) => visit(child, `${path}.content[${index}]`, depth + 1));
        }
    };
    if (!isPlainObject(value) || value.type !== 'doc') {
        return ['document must be an object with type "doc"'];
    }
    visit(value, 'doc', 0);
    return errors;
};
/** Plain text for one-line previews. */
export const richTextToPlainText = (document) => {
    const parts = [];
    const visit = (node) => {
        if (node.type === 'text' && node.text) {
            parts.push(node.text);
            return;
        }
        if (node.type === 'hardBreak') {
            parts.push(' ');
            return;
        }
        node.content?.forEach(visit);
        parts.push(' ');
    };
    if (document) {
        visit(document);
    }
    return parts.join('').replace(/\s+/g, ' ').trim();
};
export const isRichTextDocumentEmpty = (document) => {
    const hasSubstance = (node) => node.type === 'image' ||
        (node.type === 'text' && Boolean(node.text?.trim())) ||
        Boolean(node.content?.some(hasSubstance));
    return !document || !hasSubstance(document);
};
//# sourceMappingURL=richText.js.map